import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axiosConfig from "../../Service/AxiosConfig";
import Switch from "react-switch";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FaChevronDown } from "react-icons/fa";
import { PopupModal } from "../../PopupModal/PopupModal";

const WorkPreference = ({ form, setForm, fetchBooking }) => {
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [days, setDays] = useState([]);

  const [expanded, setExpanded] = useState(false);
  const [editModeWork, setEditModeWork] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [popupType, setPopupType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const firstFieldRef = useRef(null);
  const dayOptions = days.map((d) => ({
    value: d.id,
    label: d.name,
  }));

  const hasAnyData =
    form?.preferred_zones?.length > 0 ||
    form?.available_shifts?.length > 0 ||
    form?.days_available?.length > 0 ||
    form?.emergency_calls === true;

  // Auto switch to edit if no data
  useEffect(() => {
    if (!form?.id) return;
    if (!isDirty) {
      setEditModeWork(!hasAnyData);
    }
  }, [form?.id]);

  useEffect(() => {
    fetchZones();
    fetchShifts();
    fetchDays();
  }, []);

  const fetchZones = async () => {
    const res = await axiosConfig.get("/masters/preferred-zones/");
    setZones(
      (res?.data?.results || []).map((z) => ({
        value: z.id,
        label: z.name,
      })),
    );
  };

  const fetchShifts = async () => {
    const res = await axiosConfig.get("/masters/available-shifts/");
    setShifts(
      (res?.data?.results || []).map((s) => ({
        value: s.id,
        label: s.name,
      })),
    );
  };

  const fetchDays = async () => {
    try {
      const response = await axiosConfig.get("/masters/days-available/");
      setDays(response?.data?.results || []);
    } catch (err) {
      console.error("Error fetching days:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const formData = new FormData();
      (form.preferred_zones || []).forEach((id) => {
        formData.append("preferred_zones", id);
      });

      (form.available_shifts || []).forEach((id) => {
        formData.append("available_shifts", id);
      });

      (form.days_available || []).forEach((id) => {
        formData.append("days_available", id);
      });

      formData.append("emergency_calls", form.emergency_calls ? true : false);

      await axiosConfig.patch(
        `/provider-data/providers/${form?.id}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setEditModeWork(false);
      setIsDirty(false);
      setExpanded(false);

      setPopupType("success");
      setMessage("Work Preferences Saved Successfully");
      fetchBooking();
    } catch (err) {
      setPopupType("error");
      setError("Failed to save work preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["workpreferences"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("workpreferences"));
        }}
      >
        <AccordionItem uuid="workpreferences">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Work Preferences</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "0.3s",
                    marginLeft: 5,
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel className="bd-panel">
            <div className="bd-section-grid bd-grid-3 service-cards">
              <div className="form-group">
                <label
                  className={`form-label ${!editModeWork ? "view-mode-label" : ""}`}
                >
                  Preferred Zones
                </label>
                <Select
                  isMulti
                  classNamePrefix="height-select"
                  className={`${!editModeWork ? "view-mode-text" : ""}`}
                  options={zones}
                  value={zones.filter((z) =>
                    form.preferred_zones?.includes(z.value),
                  )}
                  onChange={(selected) => {
                    setIsDirty(true);
                    setForm((prev) => ({
                      ...prev,
                      preferred_zones: selected
                        ? selected.map((z) => z.value)
                        : [],
                    }));
                  }}
                  isDisabled={!editModeWork}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModeWork ? "view-mode-label" : ""}`}
                >
                  Available Shifts
                </label>
                <Select
                  isMulti
                  classNamePrefix="height-select"
                  className={`${!editModeWork ? "view-mode-text" : ""}`}
                  options={shifts}
                  value={shifts.filter((s) =>
                    form.available_shifts?.includes(s.value),
                  )}
                  onChange={(selected) => {
                    setIsDirty(true);
                    setForm((prev) => ({
                      ...prev,
                      available_shifts: selected
                        ? selected.map((s) => s.value)
                        : [],
                    }));
                  }}
                  isDisabled={!editModeWork}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModeWork ? "view-mode-label" : ""}`}
                >
                  Days Available
                </label>
                <Select
                  isMulti
                  classNamePrefix="height-select"
                  className={`${!editModeWork ? "view-mode-text" : ""}`}
                  options={dayOptions}
                  value={dayOptions.filter((option) =>
                    form.days_available?.includes(option.value),
                  )}
                  onChange={(selected) => {
                    setIsDirty(true);
                    setForm((prev) => ({
                      ...prev,
                      days_available: selected
                        ? selected.map((s) => s.value)
                        : [],
                    }));
                  }}
                  isDisabled={!editModeWork}
                />
              </div>

              <div className="form-group flex-items-data">
                <label
                  className={`form-label ${!editModeWork ? "view-mode-label" : ""}`}
                >
                  Emergency Calls?
                </label>
                <Switch
                  checked={form.emergency_calls || false}
                  onChange={(checked) => {
                    if (!editModeWork) return;
                    setIsDirty(true);
                    setForm((prev) => ({
                      ...prev,
                      emergency_calls: checked,
                    }));
                  }}
                  disabled={!editModeWork}
                />
              </div>
              <div className="bd-actions ml-css">
                {!editModeWork ? (
                  <button
                    className="bd-edit-btn"
                    onClick={() => {
                      setEditModeWork(true);
                      setExpanded(true);
                    }}
                  >
                    Edit
                  </button>
                ) : (
                  hasAnyData && (
                    <button
                      className="bd-save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      Save
                    </button>
                  )
                )}
              </div>
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>

      {popupType && (
        <PopupModal
          setpopupType={setPopupType}
          popupType={popupType}
          error={error}
          Message={message}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default WorkPreference;
